import { useSessionStore, useUserStore, useNotificationStore, useAnnouncementStore, useAppStore } from '@/stores'
import { messenger, mediator, jsonRpc } from '@communication'
import { useRemoteStream } from '@/composables'
import { useTranslation } from 'i18next-vue'

import { watch } from 'vue'

const MAX_ERROR_COUNT = 6
const NO_VIDEO_TIMEOUT = 30000

export default class VideoHealthCheckProcedure {
  constructor() {
    this.name = 'VideoHealthCheckProcedure'
    this.healthCheckInterval = null
    this.errorCount = 0
    this.lastFrameCount = 0
    this.clientCameraToggle = false
    this.triggeredDump = false
    this.lastFramesDecoded = 0
    this.lastRestartRequestAt = 0
    this.mediaDownChecks = 0
    this.mutedSince = 0
    this.reconnectingText = ''
    this.t = useTranslation().t
  }
  install = () => {
    jsonRpc.on('con:callState', this.onCallStateChanged)
    messenger.subscribe(this.name, 'strategy:muted', this.onCameraMutedChanged)
    messenger.subscribe(this.name, 'strategy:unmuted', this.onCameraUnmutedChanged)
    messenger.subscribe(this.name, 'strategy:data', this.onStrategyData)
    const session = useSessionStore()
    watch(
      () => session.client?.profile?.camera,
      state => {
        console.info(`video muted state changed: ${state}`)
        this.clientCameraToggle = state
      }
    )
  }
  uninstall = () => {
    jsonRpc.off('con:callState', this.onCallStateChanged)
    messenger.unsubscribe(this.name)
  }
  onCallStateChanged = (event) => {
    const { state } = event.detail
    if (state === 'IN_CALL') {
      this.triggeredDump = false
      this.startHealthCheck()
    } else {
      this.stopHealthCheck()
    }
  }
  startHealthCheck = () => {
    this.healthCheckInterval = setTimeout(async () => {
      const result = await this.isHealthy()
      if (result === 'healthy' || result === 'disconnected') {
        this.errorCount = 0
      } else if (result === 'unhealthy') {
        this.errorCount++
      }
      // A remote track that stays muted gets no warning (like 'suspended'), but it means that no video packet arrives.
      // Timed, not counted, as the check loops can stack (every IN_CALL starts one); acted on once per episode (-1)
      if (result !== 'muted') {
        this.mutedSince = 0
      } else if (!this.mutedSince) {
        this.mutedSince = Date.now()
      } else if (this.mutedSince > 0 && Date.now() - this.mutedSince >= NO_VIDEO_TIMEOUT) {
        this.mutedSince = -1
        this.logDiagnostics('no video packets')
        mediator.connection?.strategy.renegotiate?.()
      }
      if (this.errorCount >= MAX_ERROR_COUNT) {
        if (!this.triggeredDump) {
          this.notifyUnhealthy()
          this.logDiagnostics('video not playing')
          messenger.emit('log:dumpTrigger', { reason: 'video-unhealthy' })
          this.triggeredDump = true
        }
        this.errorCount = 0
      }
      this.startHealthCheck()
    }, 5000)
  }

  notifyUnhealthy = () => {
    console.warn('Video health check failed multiple times, notifying user to restart camera')
    const RESTART_COOLDOWN = 15000
    if (Date.now() - this.lastRestartRequestAt > RESTART_COOLDOWN) {
      const { showMessage } = useNotificationStore()
      const user = useUserStore()
      if (user.isClient) {
        showMessage({
          text: 'Camera issue detected. Please restart your camera.',
          icon: 'camera-off',
          auto: true,
        })
      } else {
        showMessage({
          text: 'Client camera issue detected. Please ask the client to restart their camera.',
          icon: 'camera-off',
          auto: true,
        })
      }
      this.lastRestartRequestAt = Date.now()
    }
  }

  // One console line with what is behind the failed checks, so a black screen can be told apart from the console
  // alone (remote log collection does not reach the server): no stream or another stream on the element, no video
  // packets arriving (negotiation or network), packets but no decoded frames, or a paused element
  logDiagnostics = async reason => {
    try {
      const video = document.querySelector('video')
      const stream = video?.srcObject
      const track = stream?.getVideoTracks?.()[0]
      const pc = mediator.connection?.strategy?.pc
      const facts = {
        reason,
        element: video && {
          paused: video.paused,
          readyState: video.readyState,
          size: `${video.videoWidth}x${video.videoHeight}`,
          hasStream: !!stream,
          isRemoteStream: !!stream && stream === useRemoteStream().video.value,
        },
        track: track && { readyState: track.readyState, muted: track.muted, enabled: track.enabled },
        clientCamera: this.clientCameraToggle,
        clientSuspended: !!this.clientSuspended,
        strategy: mediator.strategy,
        pc: pc && {
          connection: pc.connectionState,
          ice: pc.iceConnectionState,
          signaling: pc.signalingState,
          transceivers: pc.getTransceivers().map(t => `${t.mid}:${t.receiver.track.kind}:${t.direction}/${t.currentDirection}`),
        },
        inboundVideo: [],
      }
      const stats = await mediator.connection?.strategy.getMeasureStats()
      stats?.forEach(report => {
        if (report.type === 'inbound-rtp' && report.kind === 'video') {
          const { mid, packetsReceived, bytesReceived, packetsLost, framesReceived, framesDecoded, keyFramesDecoded,
            framesDropped, pliCount, firCount, nackCount, frameWidth, frameHeight, decoderImplementation } = report
          facts.inboundVideo.push({ mid, packetsReceived, bytesReceived, packetsLost, framesReceived, framesDecoded,
            keyFramesDecoded, framesDropped, pliCount, firCount, nackCount, frameWidth, frameHeight, decoderImplementation })
        } else if (report.type === 'candidate-pair' && report.nominated && report.state === 'succeeded') {
          const local = stats.get(report.localCandidateId)
          const remote = stats.get(report.remoteCandidateId)
          facts.candidatePair = `${local?.candidateType}/${local?.protocol} -> ${remote?.candidateType}/${remote?.protocol}`
        }
      })
      console.warn(`Video health check diagnostics ${JSON.stringify(facts)}`)
    } catch (error) {
      console.warn('Video health check diagnostics failed', error)
    }
  }

  stopHealthCheck = () => {
    if (this.healthCheckInterval) {
      clearTimeout(this.healthCheckInterval)
      this.healthCheckInterval = null
    }
    this.mediaDownChecks = 0
    this.mutedSince = 0
    this.showReconnecting(false)
  }

  // Hides only the text it showed, so an announcement shown by someone else meanwhile stays
  showReconnecting = show => {
    const announcement = useAnnouncementStore()
    const shown = !!this.reconnectingText && announcement.text === this.reconnectingText
    if (show && !shown) {
      this.reconnectingText = this.t('loader.reconnectAttempt')
      announcement.showMessage(this.reconnectingText, 'disconnect')
    } else if (!show && shown) {
      announcement.hideMessage()
    }
  }

  isHealthy = async () => {
    // The media connection is down (P2P peer connection restarting ICE, SFU transport lost): a network problem, not a
    // camera problem. Said in the call from the second such check on (5-10 s), so a blip or a slow start stays quiet.
    // Not in the lobby, and not during a websocket outage (mediator.connection is undefined): their own messages say it
    const mediaDown = mediator.connection?.strategy.isStrategyReady() === false
    this.mediaDownChecks = mediaDown ? this.mediaDownChecks + 1 : 0
    this.showReconnecting(this.mediaDownChecks >= 2 && !useAppStore().state.matches('app.view.meeting.lobby'))
    if (mediaDown) {
      return 'disconnected'
    }
    const video = document.querySelector('video')
    const track = video?.srcObject?.getVideoTracks()?.[0]
    if (!video || this.clientSuspended) {
      return 'suspended'
    }
    if (track?.muted) {
      return 'muted'
    }
    const isVideoLive = this.isPlaying(video) && this.isVisible(video) && this.isFrameCountIncreasing(video)
    if (!isVideoLive) {
      return 'unhealthy'
    }
    if (this.clientCameraToggle !== 'muted' && !(await this.isFrameDecoding())) {
      return 'unhealthy'
    }
    return 'healthy'
  }

  isPlaying = videoElement => {
    const isPlaying =
      !videoElement.paused &&
      !videoElement.ended &&
      videoElement.readyState >= HTMLVideoElement.HAVE_CURRENT_DATA && // >= 2
      videoElement.videoWidth > 0 &&
      videoElement.videoHeight > 0
    if (!isPlaying) {
      console.info('NOT_OK: Video is not playing')
    }
    return isPlaying
  }

  isVisible = videoElement => {
    const rect = videoElement.getBoundingClientRect()
    const isVisible =
      rect.width > 0 &&
      rect.height > 0 &&
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
      rect.left < (window.innerWidth || document.documentElement.clientWidth)
    if (!isVisible) {
      console.info('NOT_OK: Video is not visible')
    }
    return isVisible
  }

  isFrameCountIncreasing = videoElement => {
    const currentFrameCount = videoElement.getVideoPlaybackQuality().totalVideoFrames || 0
    if (this.lastFrameCount === 0) {
      this.lastFrameCount = currentFrameCount
      return true
    }
    const isIncreasing = currentFrameCount > this.lastFrameCount
    if (!isIncreasing) {
      console.info('NOT_OK: Video frame count is not increasing')
    }
    this.lastFrameCount = currentFrameCount
    return isIncreasing
  }

  isFrameDecoding = async () => {
    const stats = await mediator.connection?.strategy.getMeasureStats()
    if (!stats) return true
    let framesDecoded = null
    stats.forEach(report => {
      if (report.type === 'inbound-rtp' && report.kind === 'video') {
        framesDecoded = report.framesDecoded ?? 0
      }
    })
    if (framesDecoded === null) return true
    const isDecoding = framesDecoded > this.lastFramesDecoded
    if (!isDecoding) console.info('NOT_OK: Video frames not decoding (WebRTC stats)')
    this.lastFramesDecoded = framesDecoded
    return isDecoding
  }

  onCameraMutedChanged = ({ type }) => {
    if (type === 'video') {
      console.info('video muted')
      this.clientCameraToggle = 'muted'
    }
  }

  onCameraUnmutedChanged = ({ type }) => {
    if (type === 'video') {
      console.info('video unmuted')
      this.clientCameraToggle = 'on'
    }
  }

  onStrategyData = data => {
    if (data.type === 'video:suspended') this.clientSuspended = true
    else if (data.type === 'video:resumed') this.clientSuspended = false
  }
}
